import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { ArrowRight, Barbell, CheckCircle } from "@phosphor-icons/react";
import clsx from "clsx";
import { quizApi, type ModuleQuizStatus } from "./quizApi";

/**
 * Modul sonunda cikan pratik karti. Yumusak zorunluluk: cozulmemisse gorunur
 * ve rozet gosterir, ama modulu KILITLEMEZ.
 * Soru bankasi o modul icin hazir degilse hic gorunmez.
 */
export default function ModuleQuizCard({
  moduleId,
  status,
}: {
  moduleId: number;
  /**
   * Hazir durum verilirse kart kendi istegini ATMAZ — "Tum Dersler" 19 modul
   * listeliyor, her kartin ayri istek atmasi N+1 olurdu.
   * `null` = toplu istek henuz gelmedi (yine kendi istegini atmaz).
   * `undefined` = tek basina kullanim (kart kendi ceker).
   */
  status?: ModuleQuizStatus | null;
}) {
  const { data: fetched } = useQuery({
    queryKey: ["moduleQuizStatus", moduleId],
    queryFn: () => quizApi.status(moduleId),
    staleTime: 60 * 1000,
    retry: false,
    enabled: status === undefined,
  });

  const data = status === undefined ? fetched : status;
  if (!data?.available) return null;

  const solved = data.attempts > 0;
  // Yumusak uyari: kilitlemez, sadece derslerini bitirmeden gelen ogrenciyi uyarir
  const unitsLeft = (data.unitsTotal ?? 0) - (data.unitsDone ?? 0);

  return (
    <Link
      to={`/module-practice/${moduleId}`}
      className={clsx(
        "mt-4 flex items-center gap-3 p-4 rounded-2xl border transition-all group",
        solved
          ? "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
          : "border-myGreen/30 bg-myGreen/[0.06] hover:bg-myGreen/[0.1]",
      )}
    >
      <div
        className={clsx(
          "shrink-0 w-10 h-10 rounded-xl flex items-center justify-center",
          solved ? "bg-white/5 text-white/40" : "bg-myGreen/15 text-myGreen",
        )}
      >
        {solved ? <CheckCircle size={20} weight="fill" /> : <Barbell size={20} weight="bold" />}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">Modül Sonu Pratik</span>
          {/* Metin dogrudan buyuk harfle yazili: CSS `uppercase` Turkce'de bozuk
              calisiyor, "Yeni" -> "YENI" (noktali İ kayboluyor). */}
          {!solved && (
            <span className="text-[10px] font-bold tracking-wider bg-myGreen/20 text-myGreen px-2 py-0.5 rounded-full">
              YENİ
            </span>
          )}
        </div>
        <p className="text-xs text-white/40 mt-0.5 truncate">
          {solved
            ? lastSummary(data)
            : `${data.setSize || 20} soru — bu modül ve daha önce öğrendiğin ilgili konulardan karışık`}
        </p>
        {unitsLeft > 0 && (
          <p className="text-xs text-myOrange mt-1">
            Bu modülde henüz bitirmediğin {unitsLeft} ders var; önce onları bitirirsen sorular daha anlamlı olur.
          </p>
        )}
      </div>

      <ArrowRight
        size={16}
        weight="bold"
        className="shrink-0 text-white/30 group-hover:text-myGreen transition-colors"
      />
    </Link>
  );
}

/** "Son skorun %75 · ilk denemede 10/20 · toplam 20/20 çözüldü" — eski API'de sadece skor. */
function lastSummary(data: ModuleQuizStatus): string {
  const base = `Son skorun %${data.lastScore ?? 0}`;
  if (!data.lastTotal || data.lastFirstTryCorrect == null) return `${base} · yeni bir set çöz`;
  const first = ` · ilk denemede ${data.lastFirstTryCorrect}/${data.lastTotal}`;
  const solved =
    (data.lastSolved ?? 0) > data.lastFirstTryCorrect
      ? ` · toplam ${data.lastSolved}/${data.lastTotal} çözüldü`
      : "";
  return base + first + solved;
}
